import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { api } from '../lib/api';

const ImageUploader = ({ onAnalyzeComplete, onError }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  // Resize and compress image before upload
  // Note: Groq API has a 4MB limit for base64 encoded images
  // Base64 is ~33% larger than binary, so we need to compress to ~3MB binary (or less)
  // This means we need more aggressive compression for larger images
  const resizeAndCompressImage = (file, maxWidth = 1280, maxHeight = 1280, quality = 0.75) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }
          
          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert canvas to blob with compression
          canvas.toBlob(
            async (blob) => {
              if (!blob || blob.size === 0) {
                reject(new Error('Compressed image is empty'));
                return;
              }
              
              try {
                // Convert blob to ArrayBuffer to ensure it's fully materialized
                const arrayBuffer = await blob.arrayBuffer();
                
                // Verify we got data
                if (!arrayBuffer || arrayBuffer.byteLength === 0) {
                  reject(new Error('Failed to materialize compressed image'));
                  return;
                }
                
                // Create a new blob from the array buffer
                const materializedBlob = new Blob([arrayBuffer], { type: 'image/jpeg' });
                
                // Verify blob is valid
                if (materializedBlob.size === 0) {
                  reject(new Error('Failed to create blob from compressed image'));
                  return;
                }
                
                // Create File from the fully materialized blob
                const baseName = file.name.replace(/\.[^/.]+$/, '') || 'image';
                const compressedFile = new File([materializedBlob], `${baseName}.jpg`, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                
                // Final validation - ensure file is readable
                if (!(compressedFile instanceof File) || compressedFile.size === 0) {
                  reject(new Error('Failed to create valid file from compressed image'));
                  return;
                }
                
                // Wait a tick to ensure file is fully ready
                await new Promise(resolve => setTimeout(resolve, 0));
                
                resolve(compressedFile);
              } catch (error) {
                reject(new Error(`Failed to process compressed image: ${error.message}`));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type || !file.type.startsWith('image/')) {
        onError('Please select a valid image file.');
        return;
      }
      
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        onError('Image file is too large. Maximum size is 10MB.');
        return;
      }
      
      try {
        // Resize and compress the image
        const compressedFile = await resizeAndCompressImage(file);
        
        console.log('Image processed:', {
          originalSize: file.size,
          compressedSize: compressedFile.size,
          originalName: file.name,
          finalName: compressedFile.name,
          type: compressedFile.type,
          isFile: compressedFile instanceof File,
        });
        
        setSelectedFile(compressedFile);
        
        // Create preview from compressed file
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.onerror = () => {
          onError('Error reading file. Please try a different image.');
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('Error processing image:', error);
        // Fallback: use original file if compression fails
        console.warn('Using original file as fallback');
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    // Use the compressed file from state (already resized and compressed)
    if (!(selectedFile instanceof File)) {
      onError('Invalid file. Please select the image again.');
      return;
    }
    
    if (selectedFile.size === 0) {
      onError('File is empty. Please select a valid image.');
      return;
    }

    if (!selectedFile.type || !selectedFile.type.startsWith('image/')) {
      onError('Invalid file type. Please select an image file.');
      return;
    }

    // Verify file can be read (quick validation)
    try {
      const testReader = new FileReader();
      await new Promise((resolve, reject) => {
        testReader.onload = resolve;
        testReader.onerror = reject;
        testReader.readAsArrayBuffer(selectedFile.slice(0, 1024)); // Read first 1KB
      });
    } catch (error) {
      console.error('File validation failed:', error);
      onError('File appears to be corrupted. Please select a different image.');
      return;
    }

    setLoading(true);
    try {
      console.log('Starting upload:', {
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type,
      });
      
      // Upload the already-compressed file
      const result = await api.analyzePantryImage(selectedFile);
      onAnalyzeComplete(result);
      // Reset after successful analysis
      setSelectedFile(null);
      setPreview(null);
      // Reset file input
      const fileInput = document.getElementById('image-upload-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Error analyzing image:', error);
      // Provide user-friendly error messages
      const errorMessage = error.message || 'Failed to analyze image';
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 p-4 bg-white rounded-lg shadow">
      <div>
        <label htmlFor="image-upload-input" className="block text-sm font-medium text-gray-700 mb-2">
          Upload Pantry/Refrigerator Image
        </label>
        <input
          id="image-upload-input"
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
        />
        <p className="mt-1 text-xs text-gray-500">
          Take a photo or select an image from your device
        </p>
      </div>

      {preview && (
        <div className="flex justify-center">
          <img
            src={preview}
            alt="Preview"
            className="max-w-xs max-h-64 w-auto h-auto rounded-md border border-gray-300 object-contain"
          />
        </div>
      )}

      {selectedFile && (
        <button
          onClick={handleUpload}
          disabled={loading}
          className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {loading ? 'Analyzing Image...' : 'Analyze Image'}
        </button>
      )}
    </div>
  );
};

ImageUploader.propTypes = {
  onAnalyzeComplete: PropTypes.func.isRequired,
  onError: PropTypes.func,
};

export default ImageUploader;


